import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, ArrowRight, MessageSquare, User, Building, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useRecaptcha } from '../hooks/useRecaptcha';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactItems = [
  { icon: MapPin, label: 'Adres',           value: 'Gaston Crommenlaan 8, 9050 Gent, België' },
  { icon: Mail,   label: 'E-mail',           value: 'hello@dgitaal.be', href: 'mailto:hello@dgitaal.be' },
  { icon: Phone,  label: 'Telefoon',         value: '+32 470 41 66 75', href: 'tel:+32470416675' },
  { icon: Clock,  label: 'Openingsuren',     value: 'Ma-Vr: 09:00 - 18:00' },
];

export default function Contact() {
  const { executeRecaptcha } = useRecaptcha();
  const [form, setForm] = useState({ naam: '', bedrijf: '', email: '', telefoon: '', onderwerp: '', bericht: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const token = await executeRecaptcha('contact');
      await axios.post(`${API}/contact`, { ...form, recaptchaToken: token });
      setSuccess(true);
      setForm({ naam: '', bedrijf: '', email: '', telefoon: '', onderwerp: '', bericht: '' });
    } catch {
      setError('Er is iets misgegaan. Probeer het opnieuw of stuur een e-mail naar hello@dgitaal.be.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section
        className="relative pt-40 pb-24 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #16224b 0%, #233a80 58%, #f2a100 100%)' }}
        data-testid="contact-hero"
      >
        <div className="dot-pattern absolute inset-0" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h1 className="text-white font-bold tracking-tight mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em' }}>
            Laten we praten
          </h1>
          <p className="text-white/70 max-w-xl mx-auto text-base leading-8">
            Heeft u een vraag, wilt u een offerte of gewoon meer informatie?
            Stuur ons een bericht - wij reageren binnen 24 uur.
          </p>
        </div>
      </section>

      {/* Contact content */}
      <section className="py-24 bg-[#f8f9fd]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 items-start">

            {/* Info column */}
            <div>
              <h2 className="text-[#16224b] font-bold mb-6" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                Contactinformatie
              </h2>
              <p className="text-[#6b7184] leading-8 mb-10">
                U kunt ons bereiken via onderstaande kanalen. Wij staan klaar om u te helpen.
              </p>
              <div className="space-y-6">
                {contactItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="icon-container flex-shrink-0">
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#3d56a6] mb-1">{item.label}</p>
                      {item.href
                        ? <a href={item.href} className="text-[#16224b] font-medium hover:text-[#3d56a6] transition-colors">{item.value}</a>
                        : <p className="text-[#16224b] font-medium">{item.value}</p>
                      }
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 rounded-lg bg-white border border-[#eaecf2] p-6">
                <h3 className="text-[#16224b] font-bold mb-2">Dringende support nodig?</h3>
                <p className="text-[#6b7184] text-sm leading-7 mb-4">
                  Voor kritieke IT-problemen kunt u direct contact opnemen via ons support nummer.
                </p>
                <a href="tel:+32470416675" className="btn-outline-dark !px-6 !py-2.5 text-xs uppercase tracking-wider">
                  Bel Support
                </a>
              </div>
            </div>

            {/* Form column */}
            <div className="bg-white rounded-lg shadow-xl p-8 lg:p-10">
              {success ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-[#16224b] text-xl font-bold mb-3">Bericht verzonden!</h3>
                  <p className="text-[#6b7184]">We nemen zo snel mogelijk contact met u op.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <h2 className="text-[#16224b] font-bold" style={{ fontSize: 'clamp(1.4rem, 2.4vw, 1.9rem)' }}>
                    Stuur ons een bericht
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-[#16224b] mb-2">
                        <User className="w-3 h-3 inline mr-1" /> Naam *
                      </label>
                      <input name="naam" value={form.naam} onChange={handleChange} required
                        placeholder="Uw naam" data-testid="contact-naam" className="contact-input" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-[#16224b] mb-2">
                        <Building className="w-3 h-3 inline mr-1" /> Bedrijf
                      </label>
                      <input name="bedrijf" value={form.bedrijf} onChange={handleChange}
                        placeholder="Uw bedrijfsnaam" data-testid="contact-bedrijf" className="contact-input" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#16224b] mb-2">
                      <Mail className="w-3 h-3 inline mr-1" /> E-mail *
                    </label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required
                      placeholder="uw.naam@bedrijf.be" data-testid="contact-email" className="contact-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#16224b] mb-2">
                      <Phone className="w-3 h-3 inline mr-1" /> Telefoon
                    </label>
                    <input name="telefoon" value={form.telefoon} onChange={handleChange}
                      placeholder="+32 470 41 66 75" data-testid="contact-telefoon" className="contact-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#16224b] mb-2">
                      Onderwerp *
                    </label>
                    <select
                      name="onderwerp"
                      value={form.onderwerp}
                      onChange={handleChange}
                      required
                      data-testid="contact-onderwerp"
                      className="contact-input"
                    >
                      <option value="">Selecteer een onderwerp</option>
                      <option value="offerte">Offerteaanvraag</option>
                      <option value="support">Support</option>
                      <option value="cloud">Cloud & Azure</option>
                      <option value="security">Cybersecurity</option>
                      <option value="microsoft365">Microsoft 365</option>
                      <option value="algemeen">Algemene vraag</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#16224b] mb-2">
                      <MessageSquare className="w-3 h-3 inline mr-1" /> Bericht *
                    </label>
                    <textarea name="bericht" value={form.bericht} onChange={handleChange} required rows={5}
                      placeholder="Omschrijf uw IT-uitdaging of vraag..." data-testid="contact-bericht"
                      className="contact-input resize-none" />
                  </div>
                  {error && (
                    <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-3" data-testid="contact-error">
                      {error}
                    </p>
                  )}
                  <button type="submit" disabled={submitting}
                    className="btn-gradient w-full justify-center py-4" data-testid="contact-submit">
                    {submitting ? 'Verzenden...' : 'Verstuur bericht'} <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
